import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { usePublicData } from "@/hooks/usePublicData";
import { DEFAULT_PHONE, DEFAULT_EMAIL, SITE_ORIGIN } from "@/lib/publicSite";

export default function PrivacyPolicy() {
  const { bizInfo } = usePublicData();
  const phone = bizInfo?.business_phone || DEFAULT_PHONE;
  const email = bizInfo?.business_email || DEFAULT_EMAIL;

  useSeoHeadTags({
    title: "Privacy Policy | Sustainable Handyman Services",
    description: "Privacy Policy for Sustainable Handyman Services - How we collect, use, and protect your personal data under UK GDPR.",
    canonical: `${SITE_ORIGIN}/privacy`,
    ogType: "website",
  });

  return (
    <>
      
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <header className="bg-[#1e3a5f] text-white py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80">
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="font-semibold">Privacy Policy</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-slate max-w-none">
            <h1 className="text-3xl font-bold text-[#1e3a5f] mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">Last updated: February 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">1. Introduction</h2>
              <p>
                Sustainable Handyman Services is committed to protecting your personal data 
                and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect 
                your personal information in accordance with the UK General Data Protection Regulation (UK GDPR) 
                and the Data Protection Act 2018.
              </p>
              <p className="mt-4">
                <strong>Data Controller:</strong><br />
                Sustainable Handyman Services<br />
                Abergavenny, Monmouthshire, Wales<br />
                Email: {email}<br />
                Phone: {phone}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">2. Information We Collect</h2>
              <p>We may collect and process the following personal data:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Identity Data:</strong> Name, title</li>
                <li><strong>Contact Data:</strong> Address, email address, telephone numbers</li>
                <li><strong>Service Data:</strong> Details of services requested, job history, property information</li>
                <li><strong>Financial Data:</strong> Payment details for invoicing purposes</li>
                <li><strong>Communications Data:</strong> Your correspondence with us including enquiries and feedback</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information (when using our website)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">3. How We Use Your Information</h2>
              <p>We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>To provide our handyman and repair services</li>
                <li>To communicate with you about your enquiries and bookings</li>
                <li>To send quotes and invoices</li>
                <li>To schedule and manage appointments</li>
                <li>To improve our services based on your feedback</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">4. Legal Basis for Processing</h2>
              <p>We process your personal data based on:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Contract:</strong> To perform our contract with you for services</li>
                <li><strong>Legitimate Interests:</strong> To operate and improve our business</li>
                <li><strong>Consent:</strong> Where you have given explicit consent (e.g., marketing communications)</li>
                <li><strong>Legal Obligation:</strong> To comply with UK law</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">5. Data Retention</h2>
              <p>
                We retain your personal data only for as long as necessary for the purposes set out in this policy. 
                Typically, we keep customer records for 6 years after the last transaction for legal and tax purposes. 
                You can request deletion of your data at any time (see Your Rights below).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">6. Data Security</h2>
              <p>
                We have implemented appropriate technical and organisational measures to protect your personal data, including:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Encrypted data transmission (HTTPS/SSL)</li>
                <li>Secure password-protected systems</li>
                <li>Limited access to personal data on a need-to-know basis</li>
                <li>Regular security reviews</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">7. Data Sharing</h2>
              <p>
                We do not sell your personal data. We may share your data with:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Our team members (Sandy and Mark) who need access to provide services</li>
                <li>Service providers who assist our operations (e.g., payment processors, email services)</li>
                <li>Legal authorities if required by law</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">8. Your Rights Under UK GDPR</h2>
              <p>You have the following rights:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where consent was given</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at Sandy@sustainablehandymanservices.uk. 
                We will respond within one month.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">9. Cookies</h2>
              <p>
                Our website uses cookies to improve your experience. Please see our{" "}
                <Link to="/cookies" className="text-[#1e3a5f] underline hover:no-underline">
                  Cookie Policy
                </Link>{" "}
                for more details.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">10. Complaints</h2>
              <p>
                If you have concerns about how we handle your personal data, please contact us first. 
                You also have the right to lodge a complaint with the Information Commissioner&#39;s Office (ICO):
              </p>
              <p className="mt-2">
                <strong>Information Commissioner&#39;s Office</strong><br />
                Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF<br />
                Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline">ico.org.uk</a><br />
                Helpline: 0303 123 1113
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page 
                with an updated revision date.
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}
